/**
 * Hilfsfunktionen für die Authentifizierung
 */

/**
 * Löscht alle Auth-relevanten Einträge aus dem localStorage.
 * Muss bei Logout, 401-Fehlern und App-Reset aufgerufen werden,
 * damit nach einem Account-Wechsel keine alten User-Daten übrig bleiben.
 */
export const clearAuthStorage = () => {
  const keys = [
    'token',
    'userId',
    'schoolId',
    'user',
    'token_set_time',
    'token_check',
    'isDemo',
    'ltiContextId',
    'ltiLmsType',
    'ltiUserRole',
    'ltiResourceLinkId',
    'ltiContextLabel',
    // Aktive Lernsession des abgemeldeten Nutzers: Der naechste Nutzer auf diesem
    // Geraet darf sie nicht "erben" (Heartbeat -> 404-Toast, siehe handleUserSwitch).
    'currentSession',
  ];
  keys.forEach(key => localStorage.removeItem(key));
  sessionStorage.removeItem('token');

  // Modulweite In-Memory-Caches (siehe onAuthReset) ebenfalls leeren lassen.
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent('auth:cleared'));
  }
};

/**
 * Registriert das Leeren eines modulweiten In-Memory-Caches, der an Konto oder Schule
 * haengt. Laeuft bei clearAuthStorage (Logout, abgelaufene Sitzung, 401) und bei einem
 * Nutzerwechsel ohne Logout (handleUserSwitch).
 *
 * Hintergrund (Issue #477): Die Aura-Regeln der Schule (useAuraConfig) wurden einmal pro
 * Tab geladen und ueberlebten den Logout. Meldete sich danach im selben Tab jemand aus
 * einer anderen Schule an, sah er die Level-Schwellen der vorigen Schule.
 *
 * Warum Events statt direkter Aufrufe von hier aus: Die Caches sitzen in Hooks und
 * Services, die ueber utils/api -> tokenRefresh selbst utils/auth importieren. Ein
 * Import in die Gegenrichtung waere ein Importkreis (gleiches Muster wie der
 * SessionManager mit 'auth:userChanged').
 *
 * @param {Function} reset - leert den Cache, wird ohne Argumente aufgerufen
 * @returns {Function} meldet die Registrierung wieder ab
 */
export const onAuthReset = (reset) => {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
    return () => {};
  }
  const handler = () => reset();
  window.addEventListener('auth:cleared', handler);
  window.addEventListener('auth:userChanged', handler);
  return () => {
    window.removeEventListener('auth:cleared', handler);
    window.removeEventListener('auth:userChanged', handler);
  };
};

/**
 * Nutzerwechsel OHNE vorherigen Logout (Login/Registrierung eines anderen Kontos auf
 * demselben Geraet, z.B. Testgeraet in der Klasse). Verwirft die im Browser gespeicherte
 * Lernsession des vorherigen Nutzers und stoppt einen evtl. laufenden Heartbeat.
 *
 * Hintergrund (Prod, 2. Sep 2026): Ein Demo-Schueler hatte auf dem iPhone eine Session
 * offen; danach registrierte sich ein Lehrer derselben Schule neu. Der SessionManager
 * schickte weiter Heartbeats fuer die Demo-Session - jetzt mit dem Lehrer-Token -> der
 * Server antwortete 404 -> Toast "Session nicht gefunden" mitten im Profil-Setup.
 *
 * @param {string|null|undefined} nextUserId - ID des Nutzers, der sich gerade anmeldet
 * @returns {boolean} true, wenn ein Wechsel erkannt und aufgeraeumt wurde
 */
export const handleUserSwitch = (nextUserId) => {
  const previousUserId = localStorage.getItem('userId');
  const isSwitch = !!previousUserId && !!nextUserId && previousUserId !== nextUserId;
  if (!isSwitch) return false;

  localStorage.removeItem('currentSession');
  // SessionManager (falls geladen) stoppt darauf Heartbeat + WebSocket der alten Session,
  // Caches mit onAuthReset (z.B. Aura-Regeln der Schule) werden geleert.
  window.dispatchEvent(new CustomEvent('auth:userChanged', {
    detail: { previousUserId, nextUserId },
  }));
  return true;
};

/**
 * Dekodiert den Payload (mittleren Teil) eines JWT robust.
 *
 * WICHTIG: JWTs sind base64URL-kodiert ('-' statt '+', '_' statt '/', ohne
 * Padding). `atob()` versteht nur Standard-base64 und wirft bei '-'/'_' einen
 * `InvalidCharacterError: The string contains invalid characters`. Da FusionAuth-
 * Tokens diese Zeichen je nach Byte-Inhalt enthalten, schlug das Dekodieren
 * bisher fuer einen Teil der Nutzer fehl -> "Login fehlgeschlagen" trotz
 * korrekter Anmeldedaten und gueltigem Server-Token
 * (Sentry JAVASCRIPT-REACT-8B/DS/AF sowie GM/GK "InvalidCharacterError").
 *
 * Diese Funktion konvertiert base64URL -> base64, dekodiert UTF-8 korrekt
 * (z.B. Umlaute im Anzeigenamen) und gibt bei jedem Fehler `null` zurueck,
 * statt zu werfen.
 *
 * @param {string} token - Das komplette JWT (header.payload.signature)
 * @returns {object|null} Der dekodierte Payload oder null bei ungueltigem Token.
 */
export const decodeJwtPayload = (token) => {
  if (!token || typeof token !== 'string') {
    return null;
  }

  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }

  try {
    // base64URL -> base64 (atob akzeptiert kein '-'/'_')
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64);

    // Korrekte UTF-8-Dekodierung, mit Fallback auf den rohen atob-String,
    // falls die Byte-Sequenz nicht als UTF-8 dekodierbar ist.
    let json = binary;
    try {
      json = decodeURIComponent(
        binary
          .split('')
          .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
          .join('')
      );
    } catch {
      json = binary;
    }

    return JSON.parse(json);
  } catch (error) {
    console.warn('JWT-Payload konnte nicht dekodiert werden:', error?.message || error);
    return null;
  }
};

/**
 * Extrahiert die Benutzer-ID aus einem JWT-Token
 * @param {string} token - Das JWT-Token
 * @returns {string|null} Die Benutzer-ID, falls vorhanden
 */
export const getUserIdFromToken = (token) => {
  const payload = decodeJwtPayload(token);
  return payload?.sub || null;
}; 